import { CommonContentWrapper } from "../../common/components/content_wrapper/CommonContentWrapper";
import { Padding, Row, SizedBox } from "../../common/components/CommonBaseComponents";
import { PageTitle } from "../../common/components/PageTitle";
import SearchBar from "../../common/components/input/SearchBar";
import SecondaryButton from "../../common/components/button/SecondaryButton";
import Translate from "../../common/translate/Translate";
import PrimaryButton from "../../common/components/button/PrimaryButton";
import { useEffect, useState } from "react";
import TypeFilterButton from "../../common/components/filter_button/TypeFilterButton";
import CategoryFilterButton from "../../common/components/filter_button/CategoryFilterButton";
import { CustomTable, LinkTd } from "../../common/components/table/CustomTable";
import UserDetailModal from "./modals/UserDetailModal";
import UserRoleRequestsModal from "./modals/UserRoleRequestsModal";
import { useDevice } from "../../common/hooks/useDevice";
import { useWindowSize } from "../../common/hooks/useWindowSize";
import UserService from "../../service/UserService";
import { useSearchParams } from "react-router-dom";
import MaskUtil from "../../common/utils/MaskUtil";
import ParseUtil from "../../common/utils/ParseUtil";
import Role from "../../common/enums/Role";
import LocalStorageKey from "../../common/enums/LocalStorageKey";

export function UserListPage() {
    const service = new UserService();
    const [searchParams, setSearchParams] = useSearchParams();
    const page = searchParams.get("page") || 1;

    // 로그인한 유저 권한이 Franchise Admin이면 LG Admin 타입 필터 항목 숨김
    const currentRole = localStorage.getItem(LocalStorageKey.role);
    const typeFilterExcludeValues = currentRole === Role.FranchiseManager ? ["lgAdmin"] : [];

    const [statusTypeFilter, setStatusTypeFilter] = useState(["all"]);
    const [statusFilter, setStatusFilter] = useState(["all"]);
    const [searchFilter, setSearchFilter] = useState("phoneNumber");

    // 유저 상세 모달
    const [openDetailModal, setOpenDetailModal] = useState(false);
    const [detailDto, setDetailDto] = useState({
        type: "Shop Owner",
        id: "Hello",
        status: "Enable",
        name: "",
        contact: "",
    });

    // 롤 리퀘스트 모달
    const [openRoleRequestsModal, setOpenRoleRequestsModal] = useState(false);

    // 유저 목록 데이터
    const [userList, setUserList] = useState([]);

    const fetchUserList = (page, pageSize) => {
        const enabled = statusFilter.includes("all") ? undefined : statusFilter[0];
        service
            .getUserList({ page, pageSize, enabled })
            .then((result) => {
                const existingParams = Object.fromEntries(searchParams.entries());
                setSearchParams(
                    {
                        ...existingParams,
                        totalPage: result.page.totalPage,
                    },
                    { replace: true }
                );
                setUserList(result.users);
            })
            .catch((error) => {
                console.error("Error fetching user list:", error);
            });
    };

    useEffect(() => {
        fetchUserList(page, 20);
    }, [page, statusTypeFilter, statusFilter]);

    const handleDetailSave = () => {
        fetchUserList(page, 20);
    };

    const tableHeaders = [
        {
            key: "email",
            label: "ID",
            render: (row) => {
                const display = MaskUtil.maskEmail(row.email);
                return (
                    <LinkTd
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            setDetailDto({
                                role: row.role,
                                id: row.userNo,
                                email: row.email,
                                name: row.name,
                                contact: row.phone,
                                status: row.enabled ? "Enable" : "Disable",
                                franchiseCode: row.franchiseCode,
                                franchiseName: row.franchiseName,
                                registeredDate: row.createDate,
                                // 응답 row에 key가 아예 없는 필드는 상세에서도 숨김 (테이블 visibleHeaders와 동일 기준)
                                hasName: "name" in row,
                                hasContact: "phone" in row,
                                hasStatus: "enabled" in row,
                            });
                            setOpenDetailModal(true);
                        }}
                    >
                        {display}
                    </LinkTd>
                );
            },
        },
        {
            key: "role",
            label: "Type",
            render: (row) => ParseUtil.formatUserType(row.role),
        },
        {
            key: "name",
            label: "Name",
            render: (row) => (row.name === "" ? "-" : row.name),
        },
        {
            key: "phone",
            label: "Contact",
            render: (row) => (row.phone === "" ? "-" : row.phone),
        },
        {
            key: "enabled",
            label: "Status",
            render: (row) => (row.enabled ? "Enable" : "Disable"),
        },
        {
            key: "createDate",
            label: Translate("common.registeredDate"),
        },
    ];

    // 응답에 해당 key가 아예 없는 컬럼은 테이블에서 숨김 (admin은 name, phone, enabled 미수신)
    const optionalColumnKeys = ["name", "phone", "enabled"];
    const visibleHeaders = tableHeaders.filter((header) => {
        if (!optionalColumnKeys.includes(header.key)) return true;
        return userList.some((row) => header.key in row);
    });

    const currentDevice = useDevice();
    const { width } = useWindowSize();

    let roleRequestButton = (
        <PrimaryButton
            icon={"checked_file_white"}
            text={"Role Requests"}
            onClick={() => setOpenRoleRequestsModal(true)}
        />
    );
    let exportExcelButton = <SecondaryButton icon={"download"} text={Translate("common.exportExcel")} />;
    let pageTitle = <PageTitle title={"User Management"} />;

    if (currentDevice === "mobile" || currentDevice === "tablet" || width <= 1140) {
        roleRequestButton = (
            <PrimaryButton icon={"checked_file_white"} onClick={() => setOpenRoleRequestsModal(true)} onlyIcon />
        );
        exportExcelButton = <></>;
    }

    if (currentDevice === "mobile") {
        pageTitle = (
            <PageTitle
                title={"User Management"}
                contentLength={userList.length}
                button={currentDevice === "mobile" ? roleRequestButton : undefined}
            />
        );
    }

    const searchBar = (
        <SearchBar
            type={"userList"}
            currentValue={searchFilter}
            setValue={setSearchFilter}
            totalWidth={currentDevice === "mobile" || currentDevice === "tablet" ? "100%" : undefined}
            selectorWidth={currentDevice === "mobile" || currentDevice === "tablet" ? "20%" : undefined}
            inputWidth={currentDevice === "mobile" || currentDevice === "tablet" ? "70%" : undefined}
        />
    );
    const filterButtons = (
        <>
            <TypeFilterButton
                currentValues={statusTypeFilter}
                setValues={setStatusTypeFilter}
                type={"userManagement"}
                excludeValues={typeFilterExcludeValues}
            />
            <SizedBox width={"10px"} />
            <CategoryFilterButton currentValues={statusFilter} setValues={setStatusFilter} type={"userManagement"} />
        </>
    );

    let header = (
        <>
            {pageTitle}
            <SizedBox height={"24px"} />
            <Row justifyContent={"space-between"}>
                <Row>
                    {searchBar}
                    <SizedBox width={"10px"} />
                    {filterButtons}
                </Row>
                <Row>
                    {exportExcelButton}
                    <SizedBox width={"13px"} />
                    {roleRequestButton}
                </Row>
            </Row>
        </>
    );

    if (currentDevice === "mobile") {
        header = (
            <>
                {pageTitle}
                <SizedBox height={"20px"} />
                {searchBar}
                <SizedBox height={"12px"} />
                <Row>{filterButtons}</Row>
            </>
        );
    }

    return (
        <CommonContentWrapper hasPagination>
            <Padding padding={"24px 20px"}>
                {header}
                <SizedBox height={"20px"} />
                <CustomTable headers={visibleHeaders} data={userList} />
            </Padding>
            {openDetailModal && (
                <UserDetailModal
                    isOpen={openDetailModal}
                    onClose={() => setOpenDetailModal(false)}
                    dto={detailDto}
                    onSave={handleDetailSave}
                />
            )}
            {openRoleRequestsModal && (
                <UserRoleRequestsModal isOpen={openRoleRequestsModal} onClose={() => setOpenRoleRequestsModal(false)} />
            )}
        </CommonContentWrapper>
    );
}
